import React from "react";

const TEXT_FORMAT_BOLD = 1;
const TEXT_FORMAT_ITALIC = 1 << 1;
const TEXT_FORMAT_STRIKETHROUGH = 1 << 2;
const TEXT_FORMAT_UNDERLINE = 1 << 3;
const TEXT_FORMAT_CODE = 1 << 4;
const TEXT_FORMAT_SUBSCRIPT = 1 << 5;
const TEXT_FORMAT_SUPERSCRIPT = 1 << 6;

const applyTextFormatting = (text, format = 0) => {
  let content = text;

  if (format & TEXT_FORMAT_CODE) {
    content = (
      <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.95em]">
        {content}
      </code>
    );
  }

  if (format & TEXT_FORMAT_BOLD) {
    content = <strong>{content}</strong>;
  }

  if (format & TEXT_FORMAT_ITALIC) {
    content = <em>{content}</em>;
  }

  if (format & TEXT_FORMAT_UNDERLINE) {
    content = <u>{content}</u>;
  }

  if (format & TEXT_FORMAT_STRIKETHROUGH) {
    content = <s>{content}</s>;
  }

  if (format & TEXT_FORMAT_SUBSCRIPT) {
    content = <sub>{content}</sub>;
  }

  if (format & TEXT_FORMAT_SUPERSCRIPT) {
    content = <sup>{content}</sup>;
  }

  return content;
};

const renderLexicalNodes = (nodes = [], keyPrefix = "node") =>
  nodes.map((node, index) => renderLexicalNode(node, `${keyPrefix}-${index}`));

const renderLexicalNode = (node, key) => {
  if (!node || typeof node !== "object") return null;

  switch (node.type) {
    case "paragraph":
      return (
        <p key={key} className="leading-7 text-gray-600">
          {renderLexicalNodes(node.children, key)}
        </p>
      );

    case "heading": {
      const headingTag = typeof node.tag === "string" ? node.tag : "h2";
      const Tag = headingTag;
      const headingClasses = {
        h1: "text-3xl font-bold text-gray-900",
        h2: "text-2xl font-semibold text-gray-900",
        h3: "text-xl font-semibold text-gray-900",
        h4: "text-lg font-semibold text-gray-900",
      };

      return (
        <Tag key={key} className={headingClasses[headingTag] || headingClasses.h3}>
          {renderLexicalNodes(node.children, key)}
        </Tag>
      );
    }

    case "list": {
      const Tag = node.listType === "number" ? "ol" : "ul";
      return (
        <Tag
          key={key}
          className={
            node.listType === "number"
              ? "list-decimal space-y-2 pl-5 text-gray-600"
              : "list-disc space-y-2 pl-5 text-gray-600"
          }
        >
          {renderLexicalNodes(node.children, key)}
        </Tag>
      );
    }

    case "listitem":
      return <li key={key}>{renderLexicalNodes(node.children, key)}</li>;

    case "quote":
      return (
        <blockquote
          key={key}
          className="border-l-4 border-red-200 pl-4 italic text-gray-600"
        >
          {renderLexicalNodes(node.children, key)}
        </blockquote>
      );

    case "link": {
      const href = node.fields?.url || node.url || "#";
      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-red-600 underline-offset-4 hover:underline"
        >
          {renderLexicalNodes(node.children, key)}
        </a>
      );
    }

    case "linebreak":
      return <br key={key} />;

    case "text":
      return (
        <React.Fragment key={key}>
          {applyTextFormatting(node.text || "", node.format)}
        </React.Fragment>
      );

    default:
      if (Array.isArray(node.children)) {
        return <React.Fragment key={key}>{renderLexicalNodes(node.children, key)}</React.Fragment>;
      }

      return null;
  }
};

const PayloadRichText = ({ content, className = "" }) => {
  if (!content) return null;

  if (typeof content === "string") {
    return (
      <div className={className}>
        <p className="whitespace-pre-line leading-7 text-gray-600">{content}</p>
      </div>
    );
  }

  const root =
    Array.isArray(content) ? { children: content } : content?.root || content;
  const nodes = Array.isArray(root?.children) ? root.children : [];

  if (!nodes.length) return null;

  return <div className={`space-y-4 ${className}`.trim()}>{renderLexicalNodes(nodes)}</div>;
};

export default PayloadRichText;
